package com.corbettcode.allaboutrocks.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import kotlin.time.Duration.Companion.seconds

private val SLIDE_HEIGHT = 34.cssRem

// Posters are shown whole rather than cropped, so the caption needs its own room at the bottom
// instead of sitting over the artwork.
private val CAPTION_RESERVE = 7.cssRem

// The slides stay dark in both color modes, so the caption is always white-on-dark.
private val SlideBackdropColor = Color.rgb(0x1B1B1B)

private data class RockType(
    val name: String
    , val imageSrc: String
    , val teaser: String
    , val route: String
    // Photos crop happily; the two poster images would lose their content, so they get letterboxed.
    , val objectFit: ObjectFit = ObjectFit.Cover
)

/**
 * list of actual rock types
 */
private val rockTypes = listOf(
    RockType(
        name = "Agate"
        , imageSrc = "/images/typesof/agate/moss_agate.jpg"
        , teaser = "Banded and translucent — the stone most rockhounds learn on."
        , route = "/types_of_rocks/agate"
    )
    , RockType(
        name = "Carnelian"
        , imageSrc = "/images/typesof/carnelian/carnelian_backlit.jpg"
        , teaser = "Red-orange chalcedony that glows when you hold it up to the light."
        , route = "/types_of_rocks/carnelian"
    )
    , RockType(
        name = "Chalcedony"
        , imageSrc = "/images/typesof/chalcedony/many_faces_of_chalcedony.png"
        , teaser = "The whole waxy, cryptocrystalline quartz family in one name."
        , route = "/types_of_rocks/chalcedony"
        , objectFit = ObjectFit.Contain
    )
    , RockType(
        name = "Jasper"
        , imageSrc = "/images/typesof/jasper/Red-Jasper.webp"
        , teaser = "Opaque, richly colored quartz in every pattern imaginable."
        , route = "/types_of_rocks/jasper"
        , objectFit = ObjectFit.Contain
    )
    , RockType(
        name = "Petrified Wood"
        , imageSrc = "/images/typesof/petrifiedwood/log_cross_section.jpg"
        , teaser = "A buried tree rebuilt in silica, rings and bark and all."
        , route = "/types_of_rocks/PetrifiedWood"
    )
    , RockType(
        name = "Quartz"
        // Art still to come; until the file lands this slide shows its caption on the backdrop.
        , imageSrc = "/images/typesof/quartz/quartz-800x600.webp"
        , teaser = "Hardness 7, no cleavage, and the parent of half this site."
        , route = "/types_of_rocks/quartz"
        , objectFit = ObjectFit.Contain
    )
)

@Composable
private fun RockTypeSlide(rockType: RockType) {
    Link(
        path = rockType.route
        , modifier = Modifier
            .fillMaxWidth()
            .height(SLIDE_HEIGHT)
            .display(DisplayStyle.Block)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
            .backgroundColor(SlideBackdropColor)
        , variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
    ) {
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(0.px)
                .left(0.px)
                .right(0.px)
                .bottom(if (rockType.objectFit == ObjectFit.Contain) CAPTION_RESERVE else 0.px)
        ) {
            Image(
                src = rockType.imageSrc
                , alt = rockType.name
                , modifier = Modifier
                    .fillMaxSize()
                    .objectFit(rockType.objectFit)
            )
        }

        Column(
            modifier = Modifier
                .position(Position.Absolute)
                .left(0.px)
                .right(0.px)
                .bottom(0.px)
                // Leave the dot indicators a clear strip of their own along the bottom edge.
                .padding(top = 3.cssRem, bottom = 2.5.cssRem, leftRight = 1.25.cssRem)
                .gap(0.35.cssRem)
                // Three stops rather than two, so the caption stays readable over a bright photo.
                .backgroundImage(
                    linearGradient(LinearGradient.Direction.ToBottom) {
                        add(rgba(0, 0, 0, 0.0))
                        add(rgba(0, 0, 0, 0.6), 45.percent)
                        add(rgba(0, 0, 0, 0.9))
                    }
                )
        ) {
            SpanText(
                rockType.name
                , Modifier
                    .color(Colors.White)
                    .fontSize(1.5.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.2)
            )
            SpanText(
                rockType.teaser
                , Modifier
                    .color(rgba(255, 255, 255, 0.85))
                    .fontSize(0.95.cssRem)
                    .lineHeight(1.35)
            )
        }
    }
}

private val rockTypeSlides: List<@Composable () -> Unit> = rockTypes.map { rockType ->
    { RockTypeSlide(rockType) }
}

/**
 * A rotating showcase of the rock types covered by the site, each slide linking to its own page.
 */
@Composable
fun RockTypeCarousel(modifier: Modifier = Modifier) {
    CarouselOfSections(
        items = rockTypeSlides
        , frameDuration = 6.seconds
        , modifier = modifier
    )
}
